/** What a billing screen says when the failure's own text is not for people. */
export const BILLING_ERROR_FALLBACK =
  'Something went wrong. Please try again, and if it keeps happening, contact support@museflow.ai.';

/**
 * The server's sentences for people that come with a 5xx. Any other 5xx text is the server's or
 * the payment processor's own and is not shown.
 */
const SERVER_ERRORS_WRITTEN_FOR_PEOPLE = [
  'Subscription pricing is not configured. Please try again later.',
  'Unable to verify your existing subscription with Stripe. Please try again.',
  'Billing is temporarily unavailable.',
];

/**
 * A payment processor's object id: a short lowercase prefix (cus_, sub_sched_, ii_, acct_, ...)
 * and at least eight letters and digits, one of them a capital or a digit, so that words such as
 * in_progress or past_due are not taken for one.
 */
const PROCESSOR_ID = /\b[a-z]{2,6}(?:_[a-z]+)?_(?=[A-Za-z0-9]*[A-Z0-9])[A-Za-z0-9]{8,}/;

const looksRaw = (message: string) =>
  PROCESSOR_ID.test(message) ||
  /^no such /i.test(message) ||
  /internal server error/i.test(message);

interface FailureShape {
  isAxiosError?: boolean;
  message?: unknown;
  response?: { status?: unknown; data?: { message?: unknown } };
}

/**
 * The message a billing failure may show (MUS-628): the server's own sentence when it is written
 * for people, a message the app itself raised, or else one plain sentence. Never a payment
 * processor's text, an id, a server error or the request library's own words.
 */
export function billingErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return BILLING_ERROR_FALLBACK;
  const failure = error as FailureShape;
  const fromServer = !!failure.response || failure.isAxiosError === true;
  const message = fromServer ? failure.response?.data?.message : failure.message;
  if (typeof message !== 'string' || !message.trim() || looksRaw(message))
    return BILLING_ERROR_FALLBACK;
  const status = Number(failure.response?.status);
  if (status >= 500 && !SERVER_ERRORS_WRITTEN_FOR_PEOPLE.includes(message))
    return BILLING_ERROR_FALLBACK;
  return message;
}
